import React, { useState, useEffect } from 'react';
import { fetchRestaurantsRequest, setSelectedRestaurantId } from '../../redux/slices/restaurantSlice';
import { fetchMenuRequest } from '../../redux/slices/menuSlice';
import { FaUpload, FaTrash } from 'react-icons/fa';
import { fromBlob } from 'image-resize-compress';
import { JsonEditor } from '../common/JsonEditor';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { OperatingHoursGenerator } from './OperatingHoursGenerator';
import { convertOperatingHoursTo24Hour } from '../../utils/operatingHoursUtils';

// Operating Hours Types
interface TimeSlot {
  start: string;
  end: string;
}

interface DayScheduleData {
  firstHalf: TimeSlot;
  secondHalf: TimeSlot;
  isClosed: boolean;
}

interface OperatingHours {
  monday: DayScheduleData;
  tuesday: DayScheduleData;
  wednesday: DayScheduleData;
  thursday: DayScheduleData;
  friday: DayScheduleData;
  saturday: DayScheduleData;
  sunday: DayScheduleData;
}

// Field Section Components
interface FieldsSectionProps {
  fields: any[];
  onFieldChange: (key: string, value: any) => void;
}

const RequiredFieldsSection: React.FC<FieldsSectionProps & { requestData: Record<string, any> }> = ({ fields, onFieldChange, requestData }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const { restaurants } = useAppSelector((state) => state.restaurant);
  
  return (
    <div className="border border-[var(--border-color)] rounded-lg">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-[var(--bg-hover)] transition-colors"
      >
        <div className="flex items-center space-x-3">
          <h4 className="text-base font-semibold text-[var(--text-primary)]">Required Fields</h4>
          <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-semibold">
            {fields.length} field{fields.length !== 1 ? 's' : ''}
          </span>
        </div>
        <svg 
          className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-[var(--border-color)]">
          <div className="mt-4 grid grid-cols-2 gap-4">
            {fields.map((param: any, index: number) => (
              <div key={index} className="space-y-2">
                <div className="flex items-center space-x-2 flex-wrap">
                  <h5 className="text-sm font-medium text-[var(--accent-color)]">{param.name}</h5>
                  <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">{param.type}</span>
                  <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-semibold">required</span>
                </div>
                <p className="text-[var(--text-secondary)] text-sm">{param.description}</p>
                {(param.name === 'restaurantId' || param.name === 'restaurant_id') && restaurants.length > 0 ? (
                  <select
                    value={(() => {
                      const restaurant = restaurants.find(r => r.restaurantId === requestData[param.name]);
                      return restaurant ? restaurant.name : '';
                    })()}
                    onChange={(e) => {
                      const selectedName = e.target.value;
                      const restaurant = restaurants.find(r => r.name === selectedName);
                      if (restaurant) {
                        onFieldChange(param.name, restaurant.restaurantId);
                      }
                    }}
                    className="w-full px-3 py-2 text-sm bg-[var(--bg-primary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)] focus:border-transparent"
                  >
                    <option value="">Select restaurant</option>
                    {restaurants.map((restaurant) => (
                      <option key={restaurant.restaurantId} value={restaurant.name}>
                        {restaurant.name}
                      </option>
                    ))}
                  </select>
                ) : param.type === 'textarea' ? (
                  <textarea
                    value={requestData[param.name] || ''}
                    placeholder={`Enter ${param.name}`}
                    onChange={(e) => onFieldChange(param.name, e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 text-sm bg-[var(--bg-primary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)] focus:border-transparent"
                  />
                ) : param.type === 'select' && param.options ? (
                  <select
                    value={requestData[param.name] || ''}
                    onChange={(e) => onFieldChange(param.name, e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-[var(--bg-primary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)] focus:border-transparent"
                  >
                    <option value="">Select {param.name}</option>
                    {param.options.map((option: string) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={param.type === 'file' ? 'file' : 'text'}
                    value={param.type === 'file' ? undefined : (requestData[param.name] || '')}
                    placeholder={`Enter ${param.name}`}
                    onChange={(e) => onFieldChange(param.name, e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-[var(--bg-primary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)] focus:border-transparent"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const OptionalFieldsSection: React.FC<FieldsSectionProps & { requestData: Record<string, any> }> = ({ fields, onFieldChange, requestData }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { restaurants } = useAppSelector((state) => state.restaurant);
  
  return (
    <div className="border border-[var(--border-color)] rounded-lg">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-[var(--bg-hover)] transition-colors"
      >
        <div className="flex items-center space-x-3">
          <h4 className="text-base font-semibold text-[var(--text-primary)]">Optional Fields</h4>
          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-semibold">
            {fields.length} field{fields.length !== 1 ? 's' : ''}
          </span>
        </div>
        <svg 
          className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-[var(--border-color)]">
          <div className="mt-4 grid grid-cols-2 gap-4">
            {fields.map((param: any, index: number) => (
              <div key={index} className="space-y-2">
                <div className="flex items-center space-x-2 flex-wrap">
                  <h5 className="text-sm font-medium text-[var(--accent-color)]">{param.name}</h5>
                  <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">{param.type}</span>
                  <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">optional</span>
                </div>
                <p className="text-[var(--text-secondary)] text-sm">{param.description}</p>
                {param.type === 'textarea' ? (
                  <textarea
                    value={requestData[param.name] || ''}
                    placeholder={`Enter ${param.name}`}
                    onChange={(e) => onFieldChange(param.name, e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 text-sm bg-[var(--bg-primary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)] focus:border-transparent"
                  />
                ) : param.type === 'select' && param.options ? (
                  <select
                    value={requestData[param.name] || ''}
                    onChange={(e) => onFieldChange(param.name, e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-[var(--bg-primary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)] focus:border-transparent"
                  >
                    <option value="">Select {param.name}</option>
                    {param.options.map((option: string) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={param.type === 'file' ? 'file' : 'text'}
                    value={param.type === 'file' ? undefined : (requestData[param.name] || '')}
                    placeholder={`Enter ${param.name}`}
                    onChange={(e) => onFieldChange(param.name, e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-[var(--bg-primary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)] focus:border-transparent"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

interface ApiTestModalProps {
  isOpen: boolean;
  onClose: () => void;
  endpoint: {
    title: string;
    method: string;
    endpoint: string;
    parameters?: any;
  };
  onEndpointChange?: (newEndpoint: string) => void;
}

export const ApiTestModal: React.FC<ApiTestModalProps> = ({ 
  isOpen, 
  onClose, 
  endpoint, 
}) => {
  const dispatch = useAppDispatch();
  const { apiBaseUrl, currentApiKey, environment } = useAppSelector((state) => state.auth);
  const { restaurants, selectedRestaurantId: reduxSelectedRestaurantId } = useAppSelector((state) => state.restaurant);
  const { menuItems = [], loading: menuLoading } = useAppSelector((state) => state.menu);
  const [apiKey, setApiKey] = useState('');
  const [restaurantId, setRestaurantId] = useState('');
  const [selectedRestaurantName, setSelectedRestaurantName] = useState('');
  const [selectedProductId, setSelectedProductId] = useState('');
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [requestData, setRequestData] = useState<Record<string, any>>({});
  const [response, setResponse] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [authExpanded, setAuthExpanded] = useState(true);
  const [registerType, setRegisterType] = useState<'restaurant' | 'franchise'>('restaurant');
  const [currentEndpoint, setCurrentEndpoint] = useState(endpoint);
  const [nameSearchInput, setNameSearchInput] = useState('');
  const [showNameSuggestions, setShowNameSuggestions] = useState(false);
  const [menuType, setMenuType] = useState<'food' | 'drinks'>('food');
  
  // JSON Editor states
  const [useJsonEditor, setUseJsonEditor] = useState(false);
  const [jsonPayload, setJsonPayload] = useState('');
  
  
  // File upload states
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [compressedFiles, setCompressedFiles] = useState<File[]>([]);
  const [compressing, setCompressing] = useState(false);
  const [abortController, setAbortController] = useState<AbortController | null>(null);

  // Dynamic headers state for Order Webhook Settings
  const [webhookHeaders, setWebhookHeaders] = useState<Array<{ key: string; value: string }>>([
    { key: 'Content-Type', value: 'application/json' }
  ]);

  // Loading state for webhook config fetch
  const [webhookConfigLoading, setWebhookConfigLoading] = useState(false);

  // Operating hours state (only for Register Restaurants)
  const [operatingHours, setOperatingHours] = useState<OperatingHours>({
    monday: { firstHalf: { start: '', end: '' }, secondHalf: { start: '', end: '' }, isClosed: false },
    tuesday: { firstHalf: { start: '', end: '' }, secondHalf: { start: '', end: '' }, isClosed: false },
    wednesday: { firstHalf: { start: '', end: '' }, secondHalf: { start: '', end: '' }, isClosed: false },
    thursday: { firstHalf: { start: '', end: '' }, secondHalf: { start: '', end: '' }, isClosed: false },
    friday: { firstHalf: { start: '', end: '' }, secondHalf: { start: '', end: '' }, isClosed: false },
    saturday: { firstHalf: { start: '', end: '' }, secondHalf: { start: '', end: '' }, isClosed: false },
    sunday: { firstHalf: { start: '', end: '' }, secondHalf: { start: '', end: '' }, isClosed: false },
  });

  // Update current endpoint when prop changes
  useEffect(() => {
    setCurrentEndpoint(endpoint);
  }, [endpoint]);

  // Prepopulate API key with currentApiKey from Redux when modal opens
  useEffect(() => {
    if (isOpen && currentApiKey) {
      setApiKey(currentApiKey);
    }
  }, [isOpen, currentApiKey]);

  // Initialize restaurant selection from Redux state when modal opens
  useEffect(() => {
    if (isOpen && reduxSelectedRestaurantId && restaurants.length > 0) {
      const restaurant = restaurants.find(r => r.restaurantId === reduxSelectedRestaurantId);
      if (restaurant) {
        setRestaurantId(restaurant.restaurantId);
        setSelectedRestaurantName(restaurant.name);
        // Also update requestData for the form
        const fieldName = currentEndpoint.title === 'Register Restaurants' && registerType === 'franchise' 
          ? 'restaurant_parent_id' 
          : 'restaurant_id';
        handleInputChange(fieldName, restaurant.restaurantId);
      }
    }
  }, [isOpen, reduxSelectedRestaurantId, restaurants, currentEndpoint.title, registerType]);

  // Clear response when modal closes
  useEffect(() => {
    if (!isOpen) {
      setResponse('');
    }
  }, [isOpen]);

  // Fetch webhook config when restaurant is selected (ONLY for Order Webhook Settings)
  useEffect(() => {
    const fetchWebhookConfig = async () => {
      // Only fetch for Order Webhook Settings endpoint
      if (currentEndpoint.title !== 'Order Webhook Settings') return;
      
      // Need both API key and restaurant ID
      if (!apiKey || !restaurantId) return;

      // Set loading state and disable inputs
      setWebhookConfigLoading(true);

      try {
        const cleanEndpoint = currentEndpoint.endpoint.replace(/^api\/menu-builder/, '');
        const response = await fetch(`${apiBaseUrl}${cleanEndpoint}`, {
          method: 'GET',
          headers: {
            'API-Key': apiKey,
            'restaurant-id': restaurantId,
          },
        });

        if (response.ok) {
          const config = await response.json();
          
          // Handle response format: can be [{...}] or { data: [{...}] }
          let webhookData;
          if (Array.isArray(config) && config.length > 0) {
            // Response is directly an array
            webhookData = config[0];
          } else if (config.data && Array.isArray(config.data) && config.data.length > 0) {
            // Response has data property with array
            webhookData = config.data[0];
          } else {
            // Response is a single object
            webhookData = config;
          }
          
          // Parse headers if it's a string
          let parsedHeaders = webhookData.headers;
          if (typeof parsedHeaders === 'string') {
            try {
              parsedHeaders = JSON.parse(parsedHeaders);
            } catch (e) {
              console.error('Error parsing headers:', e);
              parsedHeaders = {};
            }
          }
          
          // Auto-populate form fields (map httpMethod to method)
          setRequestData({
            url: webhookData.url || '',
            method: webhookData.httpMethod || webhookData.http_method || webhookData.method || 'PUT',
            type: webhookData.type || 'orders',
          });
          
          // Auto-populate webhook headers
          if (parsedHeaders && typeof parsedHeaders === 'object') {
            const headersArray = Object.entries(parsedHeaders).map(([key, value]) => ({
              key,
              value: String(value),
            }));
            setWebhookHeaders(headersArray.length > 0 ? headersArray : [{ key: 'Content-Type', value: 'application/json' }]);
          }
        }
      } catch (error) {
        console.error('Error fetching webhook config:', error);
      } finally {
        // Re-enable inputs after fetch completes (success or failure)
        setWebhookConfigLoading(false);
      }
    };

    fetchWebhookConfig();
  }, [currentEndpoint.title, currentEndpoint.endpoint, apiBaseUrl, apiKey, restaurantId]);

  const getMethodBadgeStyles = (method: string) => {
    const baseStyles = 'inline-flex items-center px-3 py-1 rounded text-sm font-semibold uppercase tracking-wide';
    
    switch (method.toUpperCase()) {
      case 'GET':
        return `${baseStyles} bg-green-100 text-green-800`;
      case 'POST':
        return `${baseStyles} bg-blue-100 text-blue-800`;
      case 'PUT':
        return `${baseStyles} bg-orange-100 text-orange-800`;
      case 'PATCH':
        return `${baseStyles} bg-purple-100 text-purple-800`;
      case 'DELETE':
        return `${baseStyles} bg-red-100 text-red-800`;
      default:
        return `${baseStyles} bg-gray-100 text-gray-800`;
    }
  };

  const generateCurlCommand = () => {
    const headers = [`'API-Key: ${apiKey || '<account-id>'}'`];
    
    // Add restaurant-id header for endpoints that need it (exclude Register Restaurants and Get Restaurants)
    if (currentEndpoint.title !== 'Register Restaurants' && currentEndpoint.title !== 'Get Restaurants API') {
      headers.push(`'restaurant-id: ${restaurantId || '<restaurant-id>'}'`);
    }

    // Build request body
    let bodyData = '';
    if (currentEndpoint.parameters?.body) {
      const bodyParams: string[] = [];
      
      // For Create Order endpoint, use the requestExample from the endpoint data
      if (currentEndpoint.title === 'Create Order') {
        // Import the endpoint data to get requestExample
        import('../../data/pages/orders/createorder.json').then(data => {
          const requestExample = data.default.requestExample;
          if (requestExample) {
            bodyData = ` \\
  --header 'Content-Type: application/json' \\
  --data '${JSON.stringify(requestExample, null, 2).replace(/\n/g, '\n  ')}'`;
          }
        });
        
        // For immediate display, use a formatted structure
        bodyData = ` \\
  --header 'Content-Type: application/json' \\
  --data '{
    "name": "<customer_name>",
    "phone": "<customer_phone>",
    "email": "<customer_email>",
    "specialRequests": "<special_instructions>",
    "orderedItems": [
      {
        "name": "<item_name>",
        "quantity": "<quantity>",
        "itemPrice": "<item_price>",
        "modifiers": [
          {
            "modifier_name": "<modifier_name>",
            "modifier_price": "<modifier_price>"
          }
        ]
      }
    ]
  }'`;
      }
      // For Order Webhook Settings, handle headers object specially
      else if (currentEndpoint.title === 'Order Webhook Settings') {
        // Add url, httpMethod, type fields
        ['url', 'httpMethod', 'type'].forEach(field => {
          const value = requestData[field];
          if (value !== undefined && value !== '') {
            const escapedValue = String(value).replace(/"/g, '\\"');
            bodyParams.push(`    "${field}": "${escapedValue}"`);
          } else {
            bodyParams.push(`    "${field}": "<${field}>"`);
          }
        });
        
        // Add headers object
        const headersObj: string[] = [];
        webhookHeaders.forEach(header => {
          if (header.key && header.value) {
            headersObj.push(`      "${header.key}": "${header.value}"`);
          }
        });
        
        if (headersObj.length > 0) {
          bodyParams.push(`    "headers": {\n${headersObj.join(',\n')}\n    }`);
        } else {
          bodyParams.push(`    "headers": {\n      "Content-Type": "application/json"\n    }`);
        }
        
        if (bodyParams.length > 0) {
          bodyData = ` \\
  --header 'Content-Type: application/json' \\
  --data '{
${bodyParams.join(',\n')}
  }'`;
        }
      } else {
        // Use actual values from requestData for other endpoints
        currentEndpoint.parameters.body.forEach((param: any) => {
          const value = requestData[param.name];
          if (value !== undefined && value !== '') {
            // Escape double quotes in the value
            const escapedValue = String(value).replace(/"/g, '\\"');
            bodyParams.push(`    "${param.name}": "${escapedValue}"`);
          } else {
            // Use placeholder if no value
            bodyParams.push(`    "${param.name}": "<${param.name}>"`);
          }
        });
        
        // Add restaurantId to body if provided (for non-GET/DELETE methods)
        if (restaurantId && currentEndpoint.method !== 'GET' && currentEndpoint.method !== 'DELETE') {
          if (currentEndpoint.title === 'Register Restaurants' && registerType === 'franchise') {
            bodyParams.push(`    "restaurantParentId": "${restaurantId}"`);
          } else if (!bodyParams.some(param => param.includes('"restaurantId"'))) {
            bodyParams.push(`    "restaurantId": "${restaurantId}"`);
          }
        }
        
        if (bodyParams.length > 0) {
          bodyData = ` \\
  --header 'Content-Type: application/json' \\
  --data '{
${bodyParams.join(',\n')}
  }'`;
        }
      }
    }

    // Remove leading 'api/menu-builder' from endpoint if it exists, since apiBaseUrl already includes it
    let cleanEndpoint = currentEndpoint.endpoint.replace(/^api\/menu-builder/, '');
    
    // For GET and DELETE requests, build query string from requestData
    // For Get Restaurants API, allow restaurant_id in query params
    // For other GET/DELETE endpoints, exclude restaurant_id (it should only be in headers)
    if ((currentEndpoint.method === 'GET' || currentEndpoint.method === 'DELETE') && Object.keys(requestData).length > 0) {
      const queryParams = new URLSearchParams();
      Object.entries(requestData).forEach(([key, value]) => {
        // For Get Restaurants API, allow restaurant_id in query params
        // For other endpoints, exclude restaurant_id from query params
        if (currentEndpoint.title === 'Get Restaurants API' || key !== 'restaurant_id') {
          if (value && value !== '') {
            queryParams.append(key, value.toString());
          }
        }
      });
      const queryString = queryParams.toString();
      if (queryString) {
        cleanEndpoint += `?${queryString}`;
      }
    }
    
    return `curl --request ${currentEndpoint.method} \\
  --url ${apiBaseUrl}${cleanEndpoint} \\
  --header ${headers.join(' \\\n  --header ')}${bodyData}`;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  // Image compression helper function
  const handleBlob = async (blobFile: File) => {
    const quality = 60;
    const width = 600;
    const height = 600;
    const format = "jpeg";
    return await fromBlob(blobFile, quality, width, height, format);
  };

  // Format file size helper
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // File upload handler
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Check if it's an Excel file (only allow one Excel file)
    if (files[0].name.match(/\.(xlsx|xls)$/)) {
      setUploadedFiles([files[0]]);
      setCompressedFiles([]);
      return;
    }

    // Check if it's a PDF file (only allow one PDF file)
    if (files[0].name.match(/\.pdf$/i) || files[0].type === 'application/pdf') {
      setUploadedFiles([files[0]]);
      setCompressedFiles([]);
      return;
    }
    
    // For image files, allow multiple
    const imageFiles = files.filter(file => {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      return file.type.startsWith('image/') && allowedTypes.includes(file.type);
    });

    if (imageFiles.length > 0) {
      setUploadedFiles(imageFiles);
      
      // Compress the images in the background
      setCompressing(true);
      setCompressedFiles([]);
      
      try {
        const compressPromises = imageFiles.map(async (file) => {
          const compressedBlob = await handleBlob(file);
          return new File(
            [compressedBlob], 
            file.name, 
            { type: compressedBlob.type }
          );
        });
        
        const compressed = await Promise.all(compressPromises);
        setCompressedFiles(compressed);
      } catch (err) {
        console.error('Error compressing images:', err);
      } finally {
        setCompressing(false);
      }
    }
  };

  // Remove file handler
  const handleRemoveFile = (index: number) => {
    setUploadedFiles(prev => {
      const newFiles = [...prev];
      newFiles.splice(index, 1);
      return newFiles;
    });
    
    setCompressedFiles(prev => {
      const newFiles = [...prev];
      if (newFiles[index]) {
        newFiles.splice(index, 1);
      }
      return newFiles;
    });
    
    // If all files are removed, reset the file input
    if (uploadedFiles.length === 1) {
      const fileInput = document.getElementById('menu-upload') as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }
    }
  };

  const handleInputChange = (key: string, value: any) => {
    setRequestData(prev => ({ ...prev, [key]: value }));
    
    // Also update restaurantId state if it's the restaurant_id field
    if (key === 'restaurant_id') {
      setRestaurantId(value);
    }
  };


  const handleTest = async () => {
    // Cancel any existing request
    if (abortController) {
      abortController.abort();
    }

    // Create new abort controller for this request
    const controller = new AbortController();
    setAbortController(controller);
    
    setIsLoading(true);
    setResponse('');

    try {
      const headers: Record<string, string> = {};

      if (apiKey) {
        // Use lowercase "api-key" for Upload Menu endpoint, uppercase for others
        headers[currentEndpoint.title === 'Upload Menu' ? 'api-key' : 'API-Key'] = apiKey;
      }

      // Add restaurant-id header for all endpoints except Register Restaurants and Get Restaurants
      if (restaurantId && currentEndpoint.title !== 'Register Restaurants' && currentEndpoint.title !== 'Get Restaurants API') {
        headers['restaurant-id'] = restaurantId;
      }

      const fetchOptions: RequestInit = {
        method: currentEndpoint.method,
        headers,
        signal: controller.signal,
      };

      // Check if this is a file upload endpoint
      const isUploadEndpoint = currentEndpoint.title === 'Upload Menu' && uploadedFiles.length > 0;

      if (isUploadEndpoint) {
        // Use FormData for file uploads
        const formData = new FormData();
        
        // Check if it's an Excel or PDF file
        const isExcelFile = uploadedFiles[0].name.match(/\.(xlsx|xls)$/);
        const isPdfFile = uploadedFiles[0].name.match(/\.pdf$/i) || uploadedFiles[0].type === 'application/pdf';
        
        if (isExcelFile || isPdfFile) {
          // For Excel/PDF files, just use the original file
          formData.append('menu', uploadedFiles[0]);
        } else {
          // For image files, use compressed versions if available
          for (let i = 0; i < uploadedFiles.length; i++) {
            const file = uploadedFiles[i];
            
            if (compressedFiles[i]) {
              formData.append('menu', compressedFiles[i]);
            } else {
              // If compressed file is not available, try to compress one more time
              try {
                const compressedBlob = await handleBlob(file);
                const newCompressedFile = new File(
                  [compressedBlob], 
                  file.name, 
                  { type: compressedBlob.type }
                );
                formData.append('menu', newCompressedFile);
              } catch (err) {
                console.error('Error compressing image:', err);
                formData.append('menu', file);
              }
            }
          }
        }
        
        // Add type field instead of restaurant_id for Upload Menu
        formData.append('type', menuType);
        
        fetchOptions.body = formData;
        // Don't set Content-Type for FormData, browser will set it automatically with boundary
      } else {
        // For non-file upload endpoints
        headers['Content-Type'] = 'application/json';
        
        if (currentEndpoint.method !== 'GET') {
          let bodyData: any;
          
          // Check if JSON Editor mode is active and has valid JSON
          if (useJsonEditor && jsonPayload.trim()) {
            try {
              // Parse and use the JSON payload directly - NO VALIDATION
              bodyData = JSON.parse(jsonPayload);
              console.log('Using raw JSON payload:', bodyData);
            } catch (e) {
              // If JSON is invalid, show error and don't send
              setResponse(`Error: Invalid JSON - ${(e as Error).message}`);
              setIsLoading(false);
              setAbortController(null);
              return;
            }
          } else {
            // Use form fields (current behavior)
            bodyData = { ...requestData };
            
            // Remove any snake_case restaurant_id if it exists (we'll use camelCase restaurantId instead)
            if (bodyData.restaurant_id) {
              delete bodyData.restaurant_id;
            }
            
            // For Order Webhook Settings endpoint, build headers object from webhookHeaders state and set fixed values
            if (currentEndpoint.title === 'Order Webhook Settings') {
              const headersObj: Record<string, string> = {};
              webhookHeaders.forEach(header => {
                if (header.key && header.value) {
                  headersObj[header.key] = header.value;
                }
              });
              bodyData.headers = headersObj;
              // Set fixed values for httpMethod and type
              bodyData.httpMethod = 'PUT';
              bodyData.type = 'orders';
            }
            
            // Only add restaurantId/restaurantParentId to body for specific endpoints
            // Most endpoints use restaurant-id in headers only
            if (restaurantId && !bodyData.restaurantId) {
              if (currentEndpoint.title === 'Register Restaurants' && registerType === 'franchise') {
                // For franchise registration, add parent restaurant ID
                bodyData.restaurantParentId = restaurantId;
              }
              // For all other endpoints, restaurantId is sent in headers only, not in body
              // The only exception is Get Restaurants API which uses it as a query parameter
            }
          }
          
          // Add operating hours if any day has time slots (for Register Restaurants endpoint)
          if (currentEndpoint.title === 'Register Restaurants') {
            const hasOperatingHours = Object.values(operatingHours).some(
              day => !day.isClosed && (
                (day.firstHalf.start && day.firstHalf.end) ||
                (day.secondHalf.start && day.secondHalf.end)
              )
            );

            if (hasOperatingHours) {
              // Convert operating hours to 24-hour format before sending to API
              bodyData.operatingHours = convertOperatingHoursTo24Hour(operatingHours);
            }
          }

          if (Object.keys(bodyData).length > 0) {
            fetchOptions.body = JSON.stringify(bodyData);
          }
        }
      }

      // Remove leading 'api/menu-builder' from endpoint if it exists, since apiBaseUrl already includes it
      let cleanEndpoint = currentEndpoint.endpoint.replace(/^api\/menu-builder/, '');
      
      // For GET and DELETE requests, build query string from requestData
      // For Get Restaurants API, allow restaurant_id in query params
      // For other GET/DELETE endpoints, exclude restaurant_id (it should only be in headers)
      if ((currentEndpoint.method === 'GET' || currentEndpoint.method === 'DELETE') && Object.keys(requestData).length > 0) {
        const queryParams = new URLSearchParams();
        Object.entries(requestData).forEach(([key, value]) => {
          // For Get Restaurants API, allow restaurant_id in query params
          // For other endpoints, exclude restaurant_id from query params
          if (currentEndpoint.title === 'Get Restaurants API' || key !== 'restaurant_id') {
            if (value && value !== '') {
              queryParams.append(key, value.toString());
            }
          }
        });
        const queryString = queryParams.toString();
        if (queryString) {
          cleanEndpoint += `?${queryString}`;
        }
      }
      
      const response = await fetch(
        `${apiBaseUrl}${cleanEndpoint}`,
        fetchOptions
      );

      const responseData = await response.json();
      setResponse(JSON.stringify(responseData, null, 2));
      
      // If Register Restaurants endpoint was successful, refresh the restaurant list
      if (currentEndpoint.title === 'Register Restaurants' && response.ok) {
        console.log('Refreshing restaurant list after successful registration');
        dispatch(fetchRestaurantsRequest());
      }
      
      // If Add/Update/Delete Menu Item was successful, refresh the menu items
      if (response.ok && restaurantId && (
        currentEndpoint.title === 'Add Menu Item' || 
        currentEndpoint.title === 'Update Menu Item' || 
        currentEndpoint.title === 'Delete Menu Item'
      )) {
        console.log('Refreshing menu items after successful menu operation');
        dispatch(fetchMenuRequest({ restaurantId }));
      }
      
      // Clear request data but keep restaurant and API key for easier testing
      // Only clear restaurant for Register Restaurants endpoint (since it creates a new one)
      if (currentEndpoint.title === 'Register Restaurants') {
        setRestaurantId('');
        setSelectedRestaurantName('');
      }
      
      setSelectedProductId('');
      setSelectedProductIds([]);
      setRequestData({});
      setUploadedFiles([]);
      setCompressedFiles([]);
      setRegisterType('restaurant');
      setNameSearchInput('');
      setShowNameSuggestions(false);
      setMenuType('food');
      
      // Clear file input if it exists
      const fileInput = document.getElementById('menu-upload') as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }
    } catch (error) {
      // Don't show error if request was aborted
      if (error instanceof Error && error.name === 'AbortError') {
        console.log('Request was cancelled');
      } else {
        setResponse(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    } finally {
      setIsLoading(false);
      setAbortController(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 backdrop-blur-sm bg-white/30 flex items-center justify-center p-4">
      <div className="bg-[var(--bg-primary)] rounded-lg border border-[var(--border-color)] max-w-7xl w-full min-w-[1200px] min-h-[600px] max-h-[95vh] overflow-hidden flex flex-col">
        {/* Modal Header - Ultravox Style */}
        <div className="flex items-center justify-between p-4 border-b border-[var(--border-color)] bg-[var(--bg-secondary)]">
          {/* Left Side - Endpoint Title */}
          <div className="flex items-center space-x-3">
            <h3 className="text-lg font-semibold text-[var(--text-primary)]">
              {currentEndpoint.title}
            </h3>
          </div>
          
          {/* Right Side - Method, Endpoint and Send Button */}
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-3">
              <span className={`text-xs px-2 py-1 rounded font-semibold ${
                environment === 'sandbox' 
                  ? 'bg-amber-100 text-amber-800' 
                  : 'bg-green-100 text-green-800'
              }`}>
                {environment === 'sandbox' ? 'Sandbox' : 'Production'}
              </span>
              <span className={getMethodBadgeStyles(currentEndpoint.method)}>
                {currentEndpoint.method}
              </span>
              <code className="text-[var(--text-primary)] font-mono">
                /{currentEndpoint.endpoint.split('/').filter(Boolean).join('/')}
              </code>
              <button
                onClick={() => {
                  const cleanEndpoint = currentEndpoint.endpoint.replace(/^api\/menu-builder/, '');
                  copyToClipboard(`${apiBaseUrl}${cleanEndpoint}`);
                }}
                className="text-[var(--text-secondary)] hover:text-[var(--accent-color)] transition-colors p-1"
                title="Copy URL"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </button>
            </div>
            
            <button
              onClick={handleTest}
              disabled={isLoading}
              className="px-4 py-2 bg-[var(--accent-color)] hover:bg-[var(--accent-color)]/90 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              <span>{isLoading ? 'Sending...' : 'Send'}</span>
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M2,21L23,12L2,3V10L17,12L2,14V21Z" />
              </svg>
            </button>
            
            <button 
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        <div className="flex flex-1 overflow-hidden min-w-full min-h-[500px]">
          {/* Left Side - Authorization and Parameters */}
          <div className="flex-1 min-w-[600px] min-h-full p-6 overflow-y-auto border-r border-[var(--border-color)]">
            <div className="space-y-6">
              {currentEndpoint.title === 'Upload Menu' ? (
                <div className="text-[var(--text-secondary)] space-y-3">
                  <p>
                    This endpoint allows you to upload restaurant menus as files. The API supports uploading menu files in Excel (.xlsx), PDF (.pdf), and image formats (JPG, PNG, GIF, WebP). You can upload multiple image files in a single request.
                  </p>
                  <div className="space-y-2">
                    <h5 className="text-sm font-semibold text-[var(--text-primary)]">Authentication</h5>
                    <p className="text-sm">Include your API Key in the request headers as <code className="px-1 py-0.5 bg-[var(--bg-tertiary)] rounded text-xs">API-Key: YOUR_API_KEY</code></p>
                  </div>
                  <div className="space-y-2">
                    <h5 className="text-sm font-semibold text-[var(--text-primary)]">Request Format</h5>
                    <p className="text-sm">This endpoint accepts <code className="px-1 py-0.5 bg-[var(--bg-tertiary)] rounded text-xs">multipart/form-data</code> requests.</p>
                  </div>
                </div>
              ) : (
                <p className="text-[var(--text-secondary)]">
                  Returns account details for a single account
                </p>
              )}

              {/* Authorization Section */}
              <div className="border border-[var(--border-color)] rounded-lg">
                <button
                  onClick={() => setAuthExpanded(!authExpanded)}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-[var(--bg-hover)] transition-colors"
                >
                  <h4 className="text-base font-semibold text-[var(--text-primary)]">Authorization</h4>
                  <svg 
                    className={`w-4 h-4 transition-transform ${authExpanded ? 'rotate-180' : ''}`}
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {authExpanded && (
                  <div className="px-4 pb-4 border-t border-[var(--border-color)]">
                    <div className="mt-4 space-y-4">
                      {/* API Key Field */}
                      <div>
                        <div className="flex items-center space-x-2 mb-2">
                          <h5 className="text-sm font-medium text-[var(--accent-color)]">API-Key</h5>
                          <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">string</span>
                          <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-semibold">required</span>
                        </div>
                        <p className="text-[var(--text-secondary)] text-sm mb-3">API key</p>
                        <input
                          type="text"
                          value={apiKey}
                          onChange={(e) => setApiKey(e.target.value)}
                          placeholder="enter API-Key"
                          className="w-full px-3 py-2 text-sm bg-[var(--bg-primary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)] focus:border-transparent"
                        />
                      </div>

                      {/* Restaurant ID Field - Show for all endpoints except Register Restaurants and Get Restaurants API */}
                      {currentEndpoint.title !== 'Register Restaurants' && currentEndpoint.title !== 'Get Restaurants API' && (
                        <div>
                          <div className="flex items-center space-x-2 mb-2">
                            <h5 className="text-sm font-medium text-[var(--accent-color)]">restaurant</h5>
                            <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">select</span>
                            <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-semibold">required</span>
                          </div>
                          <p className="text-[var(--text-secondary)] text-sm mb-3">Select restaurant from your account</p>
                          {restaurants.length > 0 ? (
                            <select
                              value={selectedRestaurantName}
                              onChange={(e) => {
                                const selectedName = e.target.value;
                                setSelectedRestaurantName(selectedName);
                                const restaurant = restaurants.find(r => r.name === selectedName);
                                if (restaurant) {
                                  setRestaurantId(restaurant.restaurantId);
                                  // Save to Redux state
                                  dispatch(setSelectedRestaurantId(restaurant.restaurantId));
                                  // Fetch menu for the selected restaurant
                                  dispatch(fetchMenuRequest({ restaurantId: restaurant.restaurantId }));
                                  // For Register Restaurants in franchise mode, use restaurant_parent_id
                                  const fieldName = currentEndpoint.title === 'Register Restaurants' && registerType === 'franchise' 
                                    ? 'restaurant_parent_id' 
                                    : 'restaurant_id';
                                  handleInputChange(fieldName, restaurant.restaurantId);
                                }
                              }}
                              className="w-full px-3 py-2 text-sm bg-[var(--bg-primary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)] focus:border-transparent"
                            >
                              <option value="">Select a restaurant</option>
                              {restaurants.map((restaurant) => (
                                <option key={restaurant.restaurantId} value={restaurant.name}>
                                  {restaurant.name}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <div className="text-[var(--text-secondary)] text-sm p-3 bg-[var(--bg-secondary)] rounded border border-[var(--border-color)]">
                              No restaurants available. Please register a restaurant first.
                            </div>
                          )}
                          {restaurantId && (
                            <p className="text-[var(--text-muted)] text-xs mt-2">
                              ID: {restaurantId}
                            </p>
                          )}
                        </div>
                      )}

                    </div>
                  </div>
                )}
              </div>

              {/* File Upload Section for Upload Menu endpoint */}
              {currentEndpoint.title === 'Upload Menu' && (
                <div className="space-y-4">
                  <h4 className="text-base font-semibold text-[var(--text-primary)]">Menu Upload</h4>
                  
                  {/* Menu Type Selector */}
                  <div className="border border-[var(--border-color)] rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <h5 className="text-sm font-medium text-[var(--accent-color)]">Type</h5>
                      <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">select</span>
                      <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-semibold">required</span>
                    </div>
                    <p className="text-[var(--text-secondary)] text-sm mb-3">Select menu type</p>
                    <select
                      value={menuType}
                      onChange={(e) => setMenuType(e.target.value as 'food' | 'drinks')}
                      className="w-full px-3 py-2 text-sm bg-[var(--bg-primary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)] focus:border-transparent"
                    >
                      <option value="food">food</option>
                      <option value="drinks">drinks</option>
                    </select>
                  </div>
                  
                  <div className="border border-[var(--border-color)] rounded-lg p-4">
                    <input
                      type="file"
                      accept=".xlsx,.xls,.pdf,image/jpeg,image/png,image/gif,image/webp"
                      className="hidden"
                      onChange={handleFileSelect}
                      id="menu-upload"
                      disabled={isLoading}
                      multiple
                    />
                    
                    {uploadedFiles.length === 0 ? (
                      <label
                        htmlFor="menu-upload"
                        className="cursor-pointer flex flex-col items-center justify-center p-8 border-2 border-dashed border-[var(--border-color)] rounded-lg hover:border-[var(--accent-color)] transition-colors"
                      >
                        <FaUpload className="text-[var(--accent-color)] text-3xl mb-3" />
                        <p className="text-[var(--text-primary)] font-medium mb-1">Click to upload or drag and drop</p>
                        <p className="text-[var(--text-secondary)] text-sm">Excel files (.xlsx), PDF files (.pdf) or Images (JPG, PNG, GIF, WebP)</p>
                        <p className="text-[var(--text-secondary)] text-sm mt-1">You can select multiple images</p>
                      </label>
                    ) : (
                      <div className="space-y-3">
                        {uploadedFiles[0].name.match(/\.(xlsx|xls)$/) || uploadedFiles[0].name.match(/\.pdf$/i) || uploadedFiles[0].type === 'application/pdf' ? (
                          <div className="flex items-center justify-between p-3 bg-[var(--bg-secondary)] rounded-lg border border-[var(--border-color)]">
                            <div className="flex-1">
                              <p className="text-[var(--text-primary)] font-medium truncate">{uploadedFiles[0].name}</p>
                              <p className="text-[var(--text-secondary)] text-sm">{formatFileSize(uploadedFiles[0].size)}</p>
                            </div>
                            <button
                              onClick={() => handleRemoveFile(0)}
                              className="p-2 text-red-500 hover:text-red-600 transition-colors"
                              disabled={isLoading}
                            >
                              <FaTrash size={16} />
                            </button>
                          </div>
                        ) : (
                          uploadedFiles.map((file, index) => (
                            <div key={index} className="flex items-start space-x-3 p-3 rounded-lg border border-[var(--border-color)]">
                              <img
                                src={URL.createObjectURL(file)}
                                alt={`Preview ${index + 1}`}
                                className="w-16 h-16 object-cover rounded"
                              />
                              <div className="flex-1 min-w-0">
                                <p className="text-[var(--text-primary)] font-medium truncate">{file.name}</p>
                                <p className="text-[var(--text-secondary)] text-sm">
                                  Original: {formatFileSize(file.size)}
                                </p>
                                {compressing ? (
                                  <p className="text-[var(--text-secondary)] text-sm italic">Compressing...</p>
                                ) : compressedFiles[index] ? (
                                  <p className="text-green-500 text-sm">
                                    Compressed: {formatFileSize(compressedFiles[index].size)} 
                                    ({Math.round((compressedFiles[index].size / file.size) * 100)}% of original)
                                  </p>
                                ) : null}
                              </div>
                              <button
                                onClick={() => handleRemoveFile(index)}
                                className="p-2 text-red-500 hover:text-red-600 transition-colors flex-shrink-0"
                                disabled={isLoading}
                              >
                                <FaTrash size={16} />
                              </button>
                            </div>
                          ))
                        )}
                        
                        {isLoading && (
                          <div className="w-full bg-[var(--border-color)] rounded-full h-2">
                            <div className="bg-[var(--accent-color)] h-2 rounded-full animate-pulse" style={{ width: '100%' }}></div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Query Parameters Section - Show for GET and DELETE requests with query parameters */}
              {(currentEndpoint.method === 'GET' || currentEndpoint.method === 'DELETE') && currentEndpoint.parameters?.query && currentEndpoint.parameters.query.length > 0 && (
                <div className="space-y-4">
                  <h4 className="text-base font-semibold text-[var(--text-primary)]">Query Parameters</h4>
                  
                  {/* Multi-select Products for Delete Menu Item */}
                  {currentEndpoint.title === 'Delete Menu Item' && (
                    <div className="border border-[var(--border-color)] rounded-lg p-4">
                      <div className="space-y-3">
                        <div className="flex items-center space-x-2">
                          <h5 className="text-sm font-medium text-[var(--accent-color)]">Products to Delete</h5>
                          <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">multi-select</span>
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-semibold">recommended</span>
                        </div>
                        <p className="text-[var(--text-secondary)] text-sm">
                          Comma-separated string of primary key identifiers of menu items to delete. Multiple IDs should be separated by commas without spaces (e.g., 'id1,id2,id3')
                        </p>
                        {menuItems.length > 0 ? (
                          <>
                            {/* Search Input with Suggestions */}
                            <div className="relative">
                              <input
                                type="text"
                                placeholder="Search menu items..."
                                value={nameSearchInput}
                                onChange={(e) => setNameSearchInput(e.target.value)}
                                onFocus={() => setShowNameSuggestions(true)}
                                onBlur={() => setTimeout(() => setShowNameSuggestions(false), 200)}
                                className="w-full px-3 py-2 text-sm bg-[var(--bg-primary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)] focus:border-transparent"
                              />
                              
                              {/* Suggestions Dropdown */}
                              {showNameSuggestions && nameSearchInput && (
                                <div className="absolute z-10 w-full mt-1 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg shadow-lg max-h-[200px] overflow-y-auto">
                                  {menuItems
                                    .filter(item => 
                                      item.name.toLowerCase().includes(nameSearchInput.toLowerCase())
                                    )
                                    .map((item) => (
                                      <button
                                        key={item.pk_id}
                                        type="button"
                                        onClick={() => {
                                          if (!selectedProductIds.includes(item.pk_id)) {
                                            const newSelected = [...selectedProductIds, item.pk_id];
                                            setSelectedProductIds(newSelected);
                                            const pkIdsString = newSelected.join(',');
                                            console.log('Setting pk_ids:', pkIdsString);
                                            handleInputChange('pk_ids', pkIdsString);
                                          }
                                          setNameSearchInput('');
                                        }}
                                        className="w-full text-left px-3 py-2.5 hover:bg-[var(--bg-hover)] transition-colors border-b border-[var(--border-color)] last:border-b-0"
                                      >
                                        <div className="flex items-center justify-between">
                                          <p className="text-sm font-medium text-[var(--text-primary)] truncate flex-1">
                                            {item.name}
                                          </p>
                                          {selectedProductIds.includes(item.pk_id) && (
                                            <svg className="w-4 h-4 text-green-500 flex-shrink-0 ml-2" fill="currentColor" viewBox="0 0 20 20">
                                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                          )}
                                        </div>
                                      </button>
                                    ))}
                                  {menuItems.filter(item => 
                                    item.name.toLowerCase().includes(nameSearchInput.toLowerCase())
                                  ).length === 0 && (
                                    <div className="px-3 py-4 text-center text-sm text-[var(--text-muted)]">
                                      No items found
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            
                            {/* Selected Items as Badges */}
                            {selectedProductIds.length > 0 && (
                              <div className="flex flex-wrap gap-2 p-3 bg-[var(--bg-secondary)] rounded border border-[var(--border-color)]">
                                {selectedProductIds.map((pkId) => {
                                  const item = menuItems.find(i => i.pk_id === pkId);
                                  return (
                                    <div
                                      key={pkId}
                                      className="flex items-center space-x-2 px-3 py-1.5 bg-red-50 rounded-full text-sm border border-red-200"
                                    >
                                      <span className="font-medium text-red-700">
                                        {item?.name || pkId}
                                      </span>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const newSelected = selectedProductIds.filter(id => id !== pkId);
                                          setSelectedProductIds(newSelected);
                                          handleInputChange('pk_ids', newSelected.join(','));
                                        }}
                                        className="text-red-600 hover:bg-red-100 rounded-full p-0.5 transition-colors"
                                      >
                                        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                        </svg>
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </>
                        ) : menuLoading ? (
                          <div className="text-[var(--text-secondary)] text-sm p-3 bg-[var(--bg-secondary)] rounded border border-[var(--border-color)] flex items-center space-x-2">
                            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span>Loading menu items...</span>
                          </div>
                        ) : restaurantId ? (
                          <div className="text-[var(--text-secondary)] text-sm p-3 bg-[var(--bg-secondary)] rounded border border-[var(--border-color)]">
                            No menu items found for this restaurant. Try uploading a menu first.
                          </div>
                        ) : (
                          <div className="text-[var(--text-secondary)] text-sm p-3 bg-[var(--bg-secondary)] rounded border border-[var(--border-color)]">
                            Please select a restaurant first to load menu items
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <div className="border border-[var(--border-color)] rounded-lg p-4">
                    <div className="space-y-4">
                      {currentEndpoint.parameters.query.map((param: any, index: number) => (
                        <div key={index}>
                          <div className="flex items-center space-x-2 mb-2">
                            <h5 className="text-sm font-medium text-[var(--accent-color)]">{param.name}</h5>
                            <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">{param.type}</span>
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${
                              param.required 
                                ? 'bg-red-100 text-red-700' 
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              {param.required ? 'required' : 'optional'}
                            </span>
                          </div>
                          <p className="text-[var(--text-secondary)] text-sm mb-2">{param.description}</p>
                          {param.name === 'type' ? (
                            <select
                              value={requestData[param.name] || ''}
                              onChange={(e) => handleInputChange(param.name, e.target.value)}
                              className="w-full px-3 py-2 text-sm bg-[var(--bg-primary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)] focus:border-transparent"
                            >
                              <option value="">Select type</option>
                              <option value="food">food</option>
                              <option value="drinks">drinks</option>
                            </select>
                          ) : (param.name === 'restaurantId' || param.name === 'restaurant_id') && restaurants.length > 0 ? (
                            <div>
                              <select
                                onChange={(e) => {
                                  const selectedName = e.target.value;
                                  const restaurant = restaurants.find(r => r.name === selectedName);
                                  if (restaurant) {
                                    handleInputChange(param.name, restaurant.restaurantId);
                                  } else {
                                    handleInputChange(param.name, '');
                                  }
                                }}
                                className="w-full px-3 py-2 text-sm bg-[var(--bg-primary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)] focus:border-transparent"
                              >
                                <option value="">Select a restaurant</option>
                                {restaurants.map((restaurant) => (
                                  <option key={restaurant.restaurantId} value={restaurant.name}>
                                    {restaurant.name}
                                  </option>
                                ))}
                              </select>
                              {requestData[param.name] && (
                                <p className="text-[var(--text-muted)] text-xs mt-2">
                                  ID: {requestData[param.name]}
                                </p>
                              )}
                            </div>
                          ) : param.name === 'name' && currentEndpoint.title === 'Enrich Menu Item' ? (
                            <input
                              type="text"
                              value={requestData[param.name] || ''}
                              placeholder="Enter menu item name (e.g., Classic Margherita)"
                              onChange={(e) => handleInputChange(param.name, e.target.value)}
                              className="w-full px-3 py-2 text-sm bg-[var(--bg-primary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)] focus:border-transparent"
                            />
                          ) : param.name === 'name' && restaurants.length > 0 ? (
                            <div className="relative">
                              <input
                                type="text"
                                value={nameSearchInput}
                                placeholder="Type to search restaurant name"
                                onChange={(e) => {
                                  setNameSearchInput(e.target.value);
                                  setShowNameSuggestions(true);
                                }}
                                onFocus={() => setShowNameSuggestions(true)}
                                onBlur={() => {
                                  // Delay to allow click on suggestion
                                  setTimeout(() => setShowNameSuggestions(false), 200);
                                }}
                                className="w-full px-3 py-2 text-sm bg-[var(--bg-primary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)] focus:border-transparent"
                              />
                              {showNameSuggestions && nameSearchInput && (
                                <div className="absolute z-10 w-full mt-1 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                  {restaurants
                                    .filter(r => r.name.toLowerCase().includes(nameSearchInput.toLowerCase()))
                                    .map((restaurant) => (
                                      <button
                                        key={restaurant.restaurantId}
                                        type="button"
                                        onClick={() => {
                                          setNameSearchInput(restaurant.name);
                                          handleInputChange(param.name, restaurant.name);
                                          setShowNameSuggestions(false);
                                        }}
                                        className="w-full text-left px-3 py-2 hover:bg-[var(--bg-hover)] transition-colors text-sm text-[var(--text-primary)]"
                                      >
                                        <div className="font-medium">{restaurant.name}</div>
                                        <div className="text-xs text-[var(--text-muted)]">{restaurant.restaurantId}</div>
                                      </button>
                                    ))}
                                  {restaurants.filter(r => r.name.toLowerCase().includes(nameSearchInput.toLowerCase())).length === 0 && (
                                    <div className="px-3 py-2 text-sm text-[var(--text-muted)]">
                                      No restaurants found
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          ) : param.name === 'pk_ids' ? (
                            <input
                              type="text"
                              value={requestData['pk_ids'] || ''}
                              placeholder="Enter comma-separated pk_ids"
                              onChange={(e) => handleInputChange('pk_ids', e.target.value)}
                              className="w-full px-3 py-2 text-sm bg-[var(--bg-primary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)] focus:border-transparent"
                            />
                          ) : (
                            <input
                              type="text"
                              placeholder={`Enter ${param.name}`}
                              onChange={(e) => handleInputChange(param.name, e.target.value)}
                              className="w-full px-3 py-2 text-sm bg-[var(--bg-primary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)] focus:border-transparent"
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Parameters Section - Show for Order Webhook Settings endpoint */}
              {currentEndpoint.title === 'Order Webhook Settings' && (
                <div className="space-y-4">
                  <h4 className="text-base font-semibold text-[var(--text-primary)]">body parameter</h4>
                  
                  {/* URL Field */}
                  <div className="border border-[var(--border-color)] rounded-lg p-4">
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <h5 className="text-sm font-medium text-[var(--accent-color)]">url</h5>
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">string</span>
                        <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-semibold">required</span>
                      </div>
                      <p className="text-[var(--text-secondary)] text-sm">The webhook destination URL where order notifications will be sent</p>
                      <div className="flex items-center border border-[var(--border-color)] rounded bg-[var(--bg-primary)] focus-within:ring-2 focus-within:ring-[var(--accent-color)] focus-within:border-transparent overflow-hidden">
                        <span className="px-3 py-2 bg-[var(--bg-secondary)] text-[var(--text-primary)] text-sm font-semibold border-r border-[var(--border-color)]">
                          POST
                        </span>
                        <input
                          type="text"
                          value={requestData.url || ''}
                          placeholder={webhookConfigLoading ? "Loading webhook URL..." : "https://your-webhook-url.com/endpoint"}
                          onChange={(e) => handleInputChange('url', e.target.value)}
                          disabled={webhookConfigLoading}
                          className="flex-1 px-3 py-2 text-sm bg-[var(--bg-primary)] text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none border-0 disabled:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-500"
                        />
                      </div>
                    </div>
                  </div>


                  {/* Headers Field - Dynamic Key-Value Pairs */}
                  <div className="border border-[var(--border-color)] rounded-lg p-4">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <h5 className="text-sm font-medium text-[var(--accent-color)]">headers</h5>
                          <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">object</span>
                          <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-semibold">required</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setWebhookHeaders([...webhookHeaders, { key: '', value: '' }]);
                          }}
                          disabled={webhookConfigLoading}
                          className="px-3 py-1.5 bg-[var(--accent-color)] text-white rounded text-xs font-medium hover:opacity-90 transition-colors flex items-center space-x-1 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                          </svg>
                          <span>Add Header</span>
                        </button>
                      </div>
                      <p className="text-[var(--text-secondary)] text-sm">
                        Custom headers to include in webhook requests. Content-Type: application/json is required. You can add additional custom key-value pairs as needed.
                      </p>
                      
                      {/* Headers List */}
                      <div className="space-y-2">
                        {webhookHeaders.map((header, index) => (
                          <div key={index} className="flex items-center space-x-2">
                            <input
                              type="text"
                              value={header.key}
                              placeholder={webhookConfigLoading ? "Loading..." : "Header name"}
                              onChange={(e) => {
                                const newHeaders = [...webhookHeaders];
                                newHeaders[index].key = e.target.value;
                                setWebhookHeaders(newHeaders);
                              }}
                              disabled={header.key === 'Content-Type' || webhookConfigLoading}
                              className="flex-1 px-3 py-2 text-sm bg-[var(--bg-primary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                            />
                            <input
                              type="text"
                              value={header.value}
                              placeholder={webhookConfigLoading ? "Loading..." : "Header value"}
                              onChange={(e) => {
                                const newHeaders = [...webhookHeaders];
                                newHeaders[index].value = e.target.value;
                                setWebhookHeaders(newHeaders);
                              }}
                              disabled={header.key === 'Content-Type' || webhookConfigLoading}
                              className="flex-1 px-3 py-2 text-sm bg-[var(--bg-primary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                            />
                            {header.key !== 'Content-Type' && (
                              <button
                                type="button"
                                onClick={() => {
                                  const newHeaders = webhookHeaders.filter((_, i) => i !== index);
                                  setWebhookHeaders(newHeaders);
                                }}
                                disabled={webhookConfigLoading}
                                className="p-2 text-red-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Remove header"
                              >
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                              </button>
                            )}
                            {header.key === 'Content-Type' && (
                              <div className="w-10"></div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                </div>
              )}

              {/* Parameters Section - Show for Register Restaurants, Menu Item, Payment Status Update, Order Status Update, Merchant Configuration, and Voice Configuration endpoints */}
              {(currentEndpoint.title === 'Register Restaurants' || 
                currentEndpoint.title === 'Add Menu Item' || 
                currentEndpoint.title === 'Update Menu Item' ||
                currentEndpoint.title === 'Upsert Menu Item' ||
                currentEndpoint.title === 'Payment Status Update' ||
                currentEndpoint.title === 'Order Status Update' ||
                currentEndpoint.title === 'Merchant Configuration' ||
                currentEndpoint.title === 'Get Voice Configuration' ||
                currentEndpoint.title === 'Update Voice Configuration') && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-base font-semibold text-[var(--text-primary)]">
                      body parameter
                    </h4>
                    {/* Toggle between Form and JSON Editor - Only show for Upsert Menu Item */}
                    {currentEndpoint.title === 'Upsert Menu Item' && (
                      <div className="flex items-center space-x-2 bg-[var(--bg-secondary)] rounded-lg p-1">
                        <button
                          onClick={() => setUseJsonEditor(true)}
                          className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                            useJsonEditor
                              ? 'bg-[var(--accent-color)] text-white'
                              : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                          }`}
                        >
                          {'{}'} JSON Editor
                        </button>
                        <button
                          onClick={() => setUseJsonEditor(false)}
                          className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                            !useJsonEditor
                              ? 'bg-[var(--accent-color)] text-white'
                              : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                          }`}
                        >
                          📝 Form Fields
                        </button>
                      </div>
                    )}
                    {/* {currentEndpoint.title === 'Register Restaurants' && (
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="createFranchise"
                          checked={registerType === 'franchise'}
                          onChange={(e) => setRegisterType(e.target.checked ? 'franchise' : 'restaurant')}
                          className="w-4 h-4 text-red-600 bg-white border-gray-300 rounded focus:ring-red-500 focus:ring-2"
                        />
                        <label 
                          htmlFor="createFranchise" 
                          className="text-sm font-medium text-[var(--text-primary)] cursor-pointer select-none"
                        >
                          Create Franchise
                        </label>
                      </div>
                    )} */}
                  </div>
                  
                  {/* Products Dropdown for Update Menu Item */}
                  {currentEndpoint.title === 'Update Menu Item' && (
                    <div className="border border-[var(--border-color)] rounded-lg p-4">
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <h5 className="text-sm font-medium text-[var(--accent-color)]">Products</h5>
                          <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">select</span>
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-semibold">recommended</span>
                        </div>
                        <p className="text-[var(--text-secondary)] text-sm mb-3">
                          Select a food item to populate the form with existing data
                        </p>
                        {menuItems.length > 0 ? (
                          <>
                            <select
                              value={selectedProductId}
                              onChange={(e) => {
                                const productId = e.target.value;
                                setSelectedProductId(productId);
                                
                                if (productId) {
                                  const product = menuItems.find(item => item.pk_id === productId);
                                  if (product) {
                                    // Populate all form fields with product data
                                    setRequestData({
                                      pk_id: product.pk_id,
                                      name: product.name || '',
                                      modifiers: product.modifiers || '',
                                      price: product.price || '',
                                      large_price: product.large_price || '',
                                      image: product.image || '',
                                      product_description: product.product_description || '',
                                      level1_category: product.level1_category || '',
                                      level2_category: product.level2_category || '',
                                      Nutrients: product.Nutrients || '',
                                      appearance: product.appearance || '',
                                      ingredients: product.ingredients || '',
                                      serving: product.serving || '',
                                      flavors: product.flavors || '',
                                      variations: product.variations || '',
                                      allergy_info: product.allergy_info || '',
                                      Calories: product.Calories || '',
                                      is_vegan: product.is_vegan || '',
                                      food_type: product.food_type || '',
                                      is_spice_applicable: product.is_spice_applicable || '',
                                    });
                                  }
                                } else {
                                  // Clear form if no product selected
                                  setRequestData({});
                                }
                              }}
                              className="w-full px-3 py-2 text-sm bg-[var(--bg-primary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)] focus:border-transparent"
                            >
                              <option value="">Select a product to edit</option>
                              {menuItems.map((item) => (
                                <option key={item.pk_id} value={item.pk_id}>
                                  {item.name} - {item.price}
                                </option>
                              ))}
                            </select>
                            {selectedProductId && (
                              <p className="text-[var(--text-muted)] text-xs mt-2">
                                Product ID: {selectedProductId}
                              </p>
                            )}
                          </>
                        ) : menuLoading ? (
                          <div className="text-[var(--text-secondary)] text-sm p-3 bg-[var(--bg-secondary)] rounded border border-[var(--border-color)] flex items-center space-x-2">
                            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span>Loading menu items...</span>
                          </div>
                        ) : restaurantId ? (
                          <div className="text-[var(--text-secondary)] text-sm p-3 bg-[var(--bg-secondary)] rounded border border-[var(--border-color)]">
                            No menu items found for this restaurant. Try uploading a menu first.
                          </div>
                        ) : (
                          <div className="text-[var(--text-secondary)] text-sm p-3 bg-[var(--bg-secondary)] rounded border border-[var(--border-color)]">
                            Please select a restaurant first to load menu items
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Restaurant ID Field (in payload for Register Restaurants endpoint only when creating franchise) */}
                  {(currentEndpoint.title === 'Register Restaurants' && registerType === 'franchise') && (
                    <div className="border border-[var(--border-color)] rounded-lg p-4">
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <h5 className="text-sm font-medium text-[var(--accent-color)]">restaurant</h5>
                          <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">select</span>
                          <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-semibold">required</span>
                        </div>
                        <p className="text-[var(--text-secondary)] text-sm mb-3">
                          Select parent restaurant for franchise creation
                        </p>
                        {restaurants.length > 0 ? (
                          <>
                            <select
                              value={selectedRestaurantName}
                              onChange={(e) => {
                                const selectedName = e.target.value;
                                setSelectedRestaurantName(selectedName);
                                const restaurant = restaurants.find(r => r.name === selectedName);
                                if (restaurant) {
                                  setRestaurantId(restaurant.restaurantId);
                                  handleInputChange('restaurant_parent_id', restaurant.restaurantId);
                                }
                              }}
                              className="w-full px-3 py-2 text-sm bg-[var(--bg-primary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)] focus:border-transparent"
                            >
                              <option value="">Select a restaurant</option>
                              {restaurants.map((restaurant) => (
                                <option key={restaurant.restaurantId} value={restaurant.name}>
                                  {restaurant.name}
                                </option>
                              ))}
                            </select>
                            {restaurantId && (
                              <p className="text-[var(--text-muted)] text-xs mt-2">
                                Parent ID: {restaurantId}
                              </p>
                            )}
                          </>
                        ) : (
                          <div className="text-[var(--text-secondary)] text-sm p-3 bg-[var(--bg-secondary)] rounded border border-[var(--border-color)]">
                            No restaurants available. Please register a restaurant first.
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Show Form Fields or JSON Editor based on toggle */}
                  {useJsonEditor ? (
                    /* JSON Editor Mode - Using JsonEditor Component */
                    <div>
                      <p className="text-[var(--text-secondary)] text-sm mb-3">
                        Paste your complete JSON payload here. It will be sent as-is without any validation or modification.
                      </p>
                      <JsonEditor
                        value={jsonPayload || '{}'}
                        onChange={(parsed) => {
                          setJsonPayload(JSON.stringify(parsed, null, 2));
                        }}
                        height="400px"
                        readOnly={false}
                        showFormatButton={true}
                        showCopyButton={true}
                        theme="light"
                      />
                    </div>
                  ) : (
                    /* Form Fields Mode */
                    <>
                      {/* Required Fields Section */}
                      {currentEndpoint.parameters?.body && currentEndpoint.parameters.body.some((param: any) => param.required) && (
                        <RequiredFieldsSection 
                          fields={currentEndpoint.parameters.body.filter((param: any) => {
                            // Hide restaurantId ONLY for Upsert Menu Item
                            if (currentEndpoint.title === 'Upsert Menu Item' && param.name === 'restaurantId') {
                              return false;
                            }
                            return param.required;
                          })}
                          onFieldChange={handleInputChange}
                          requestData={requestData}
                        />
                      )}

                      {/* Operating Hours Section - Only for Register Restaurants */}
                      {currentEndpoint.title === 'Register Restaurants' && (
                        <OperatingHoursGenerator
                          onChange={(hours) => setOperatingHours(hours)}
                          initialHours={operatingHours}
                        />
                      )}
                      
                      {/* Optional Fields Section */}
                      {currentEndpoint.parameters?.body && currentEndpoint.parameters.body.some((param: any) => !param.required) && (
                        <OptionalFieldsSection 
                          fields={currentEndpoint.parameters.body.filter((param: any) => {
                            // Hide restaurantId ONLY for Upsert Menu Item
                            if (currentEndpoint.title === 'Upsert Menu Item' && param.name === 'restaurantId') {
                              return false;
                            }
                            return !param.required;
                          })}
                          onFieldChange={handleInputChange}
                          requestData={requestData}
                        />
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right Side - cURL and Response Split */}
          <div className="w-96 min-w-[384px] min-h-full flex-shrink-0 flex flex-col">
            {/* Top Half - cURL */}
            <div className="flex-1 min-h-0 max-h-[500px] bg-[var(--bg-secondary)] p-4 border-b border-[var(--border-color)]">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-[var(--text-primary)]">cURL</h4>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => copyToClipboard(generateCurlCommand())}
                    className="text-[var(--text-secondary)] hover:text-[var(--accent-color)] transition-colors p-1"
                    title="Copy"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="bg-[var(--bg-tertiary)] rounded-lg border border-[var(--border-color)] p-4 h-full overflow-auto">
                <pre className="text-sm text-[var(--text-primary)] font-mono whitespace-pre-wrap">
                  <code>{generateCurlCommand()}</code>
                </pre>
              </div>
            </div>

            {/* Bottom Half - Response */}
            <div className="flex-1 min-h-0 max-h-[400px] bg-[var(--bg-secondary)] p-4 flex flex-col">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-[var(--text-primary)]">
                  {isLoading ? 'Loading Response...' : response ? '200' : 'Response'}
                </h4>
                <div className="flex items-center space-x-2">
                  {response && !isLoading && (
                    <button
                      onClick={() => navigator.clipboard.writeText(response)}
                      className="text-[var(--text-secondary)] hover:text-[var(--accent-color)] transition-colors p-1"
                      title="Copy response"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
              <div className="flex-1 bg-[var(--bg-tertiary)] rounded-lg border border-[var(--border-color)] p-4 overflow-y-auto">
                {isLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="flex items-center space-x-2 text-[var(--text-secondary)]">
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className="text-sm">Loading response...</span>
                    </div>
                  </div>
                ) : response ? (
                  <pre className="text-sm text-[var(--text-primary)] font-mono whitespace-pre-wrap break-words">
                    <code>{response}</code>
                  </pre>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-[var(--text-muted)] text-sm">
                      Click Send to test the API endpoint
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
